import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2, AlertCircle, Target, TrendingUp, Printer } from 'lucide-react';

const API_URL = 'https://api.glowytics.com';
const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';

const DATE_RANGES = [
  { value: 'all', label: 'Tüm zamanlar (kampanya ömrü)' },
  { value: 'last90', label: 'Son 90 gün' },
  { value: 'last30', label: 'Son 30 gün' },
  { value: 'thisMonth', label: 'Bu ay' },
  { value: 'lastMonth', label: 'Geçen ay' },
];

// ── Plan rakamları — /budget/canon-nisan-2026 ile birebir. PLAN TARİHLERİ KASITLI YOK SAYILIR;
//    eşleştirme yalnızca kampanya İSMİNDEN yapılır (kullanıcı: planlardaki tarihlere uyulmadı). ──
function buildPlan() {
  // 1) Get Ready / Hayal Et  (NET 122.400)
  const NET = 122400;
  const aw = { budget: 47000, cpm: 37.5, reach: 700000, clicks: 4500 };
  const en = { budget: 42000, cpm: 50, reach: 450000, clicks: 5700 };
  const re = { budget: NET - 47000 - 42000, cpm: 22.5, reach: 900000, clicks: 3400 };
  const impr = (x) => Math.round((x.budget / x.cpm) * 1000);
  const hayalEt = {
    spend: NET,
    impressions: impr(aw) + impr(en) + impr(re),
    reach: aw.reach + en.reach + re.reach,
    clicks: aw.clicks + en.clicks + re.clicks,
  };

  // 2) Workshop  (WS_NET 33.150)
  const WS_NET = 33150;
  const ws = [
    { budget: Math.round(WS_NET * 0.5), cpm: 45, cpc: 3.5 },
    { budget: Math.round(WS_NET * 0.25), cpm: 48, cpc: 3.8 },
  ];
  ws.push({ budget: WS_NET - ws[0].budget - ws[1].budget, cpm: 48, cpc: 3.8 });
  const workshop = ws.reduce(
    (t, w) => {
      const i = Math.round((w.budget / w.cpm) * 1000);
      t.impressions += i;
      t.reach += Math.round(i * 0.55);
      t.clicks += Math.round(w.budget / w.cpc);
      return t;
    },
    { spend: WS_NET, impressions: 0, reach: 0, clicks: 0 }
  );

  // 3) Megatank / Değerli Kağıt (ValuablePaper)  (MT_NET 158.950) — orijinal 7 ad set planı
  const MT_NET = 158950;
  const rival = Math.round(MT_NET * 0.5);
  const vp = MT_NET - rival;
  const CPM_VIDEO = 58, CPM_STATIC = 43;
  const mtSets = [
    { budget: Math.round(rival * 0.3), cpm: CPM_VIDEO, video: true },
    { budget: Math.round(rival * 0.3), cpm: CPM_VIDEO, video: true },
    { budget: Math.round(rival * 0.2), cpm: CPM_STATIC, video: false },
    { budget: rival - Math.round(rival * 0.3) * 2 - Math.round(rival * 0.2), cpm: CPM_STATIC, video: false },
    { budget: Math.round(vp * 0.35), cpm: CPM_VIDEO, video: true },
    { budget: Math.round(vp * 0.35), cpm: CPM_VIDEO, video: true },
    { budget: vp - Math.round(vp * 0.35) * 2, cpm: CPM_STATIC, video: false },
  ];
  const megatank = mtSets.reduce(
    (t, a) => {
      const i = Math.round((a.budget / a.cpm) * 1000);
      t.impressions += i;
      t.reach += Math.round(i * 0.55);
      if (!a.video) t.clicks += Math.round(i * 0.008); // statik ~%0.8 CTR varsayımı
      return t;
    },
    { spend: MT_NET, impressions: 0, reach: 0, clicks: 0 }
  );

  // 4) İFSAK Atölyesi — Acil Push  (IF_NET 7.650)
  const IF_NET = 7650;
  const ifsak = {
    spend: IF_NET,
    impressions: Math.round((IF_NET / 65) * 1000),
    reach: Math.round(((IF_NET / 65) * 1000) * 0.55),
    clicks: Math.round(IF_NET / 4.8),
  };

  return [
    { key: 'hayalet', title: 'Get Ready — Hayal Et', note: 'Awareness + Engagement + Reach', plan: hayalEt },
    { key: 'workshop', title: 'Workshop Kayıt Kampanyası', note: 'Çekim Atölyeleri — trafik', plan: workshop },
    { key: 'megatank', title: 'Megatank / Değerli Kağıt', note: 'ValuablePaper bilinirlik', plan: megatank },
    { key: 'ifsak', title: 'İFSAK Atölyesi', note: 'Acil push', plan: ifsak },
  ];
}

// Türkçe diakritikleri sadeleştirip isimle güvenli eşleştirme (İ/ç/ö/ş/ğ sorunlarını çözer)
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

// Öncelik sırası önemli: workshop, hayalet'ten ÖNCE (workshop isimleri de "HayalEt" içerir)
function classify(name) {
  const n = norm(name);
  if (n.includes('ifsak')) return 'ifsak';
  // workshop/çekim atölyesi: atölye, çekim + lokasyon "Camlı Köşk" (camkosk) ve "düğün" (dugun) kampanyaları
  if (n.includes('atolye') || n.includes('cekim') || n.includes('camkosk') || n.includes('dugun')) return 'workshop';
  if (n.includes('valuablepaper') || n.includes('megatank') || n.includes('degerli') || n.includes('rival')) return 'megatank';
  if (n.includes('hayalet')) return 'hayalet';
  return null; // eşleşmeyen
}

const fmtInt = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 });
const fmtMoney = (n) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 }) + ' ₺';
const fmtNum = (n, d = 2) => Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtPct = (n) => fmtNum(n, 2) + '%';
// CPM/CPC için küsuratlı para formatı (2 ondalık)
const fmtMoney2 = (n) => fmtNum(n, 2) + ' ₺';

const METRIC_ROWS = [
  { key: 'spend', label: 'Harcama / Bütçe', fmt: fmtMoney, kind: 'budget' },
  { key: 'impressions', label: 'Gösterim', fmt: fmtInt, kind: 'volume' },
  { key: 'reach', label: 'Erişim', fmt: fmtInt, kind: 'volume' },
  { key: 'clicks', label: 'Tıklama', fmt: fmtInt, kind: 'volume' },
  { key: 'ctr', label: 'CTR', fmt: fmtPct, kind: 'ratio_high' },
  { key: 'cpm', label: 'CPM', fmt: fmtMoney2, kind: 'ratio_low' },
  { key: 'cpc', label: 'CPC', fmt: fmtMoney2, kind: 'ratio_low' },
];

// türetilmiş oranları topla
function deriveRatios(o) {
  return {
    ...o,
    ctr: o.impressions > 0 ? (o.clicks / o.impressions) * 100 : 0,
    cpm: o.impressions > 0 ? (o.spend / o.impressions) * 1000 : 0,
    cpc: o.clicks > 0 ? o.spend / o.clicks : 0,
  };
}

export default function CanonPlanVsActual() {
  const [dateRange, setDateRange] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resp, setResp] = useState(null);

  const planGroups = useMemo(() => buildPlan(), []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${API_URL}/api/meta-ads/live/campaign-insights`, {
        params: { dateRange },
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        withCredentials: true,
        timeout: 120000,
      });
      setResp(r.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'İstek başarısız');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  // Kampanyaları gruplara dağıt
  const grouped = useMemo(() => {
    const buckets = {};
    for (const g of planGroups) buckets[g.key] = [];
    const unmatched = [];
    for (const c of (resp?.campaigns || [])) {
      const k = classify(c.campaign_name);
      if (k && buckets[k]) buckets[k].push(c);
      else unmatched.push(c);
    }
    return { buckets, unmatched };
  }, [resp, planGroups]);

  const sumGroup = (camps) => deriveRatios(camps.reduce((t, c) => ({
    spend: t.spend + Number(c.spend || 0),
    impressions: t.impressions + Number(c.impressions || 0),
    reach: t.reach + Number(c.reach || 0),
    clicks: t.clicks + Number(c.clicks || 0),
  }), { spend: 0, impressions: 0, reach: 0, clicks: 0 }));

  const win = resp?.window;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* başlık */}
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-6 h-6" style={{ color: '#CC0000' }} />
        <h1 className="text-2xl font-bold text-gray-900">Canon — Plan vs Gerçekleşen</h1>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Kampanya isimlerinden eşleştirilir, gerçekleşen veriler <strong>canlı</strong> Meta API'den çekilir.
        Plan tarihleri yok sayılır — her kampanyanın gerçek ömrü baz alınır.
      </p>

      {/* kontroller */}
      <div className="flex flex-wrap items-end gap-3 mb-6 print:hidden">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Gerçekleşen veri aralığı</label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-500"
          >
            {DATE_RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 text-white font-semibold px-5 py-2 rounded-lg text-sm disabled:opacity-60"
          style={{ backgroundColor: '#CC0000' }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {loading ? 'Çekiliyor…' : 'Yenile'}
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-50"
        >
          <Printer className="w-4 h-4" /> Yazdır / PDF
        </button>
        {win && <span className="text-xs text-gray-400 ml-auto">Veri: {win.since} → {win.until}{resp?.accountId ? ` · ${resp.accountId}` : ''}</span>}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      {loading && !resp && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-10 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Canlı veri çekiliyor…
        </div>
      )}

      {resp && (
        <div className="space-y-6">
          {planGroups.map((g) => {
            const camps = grouped.buckets[g.key] || [];
            const actual = sumGroup(camps);
            const plan = deriveRatios(g.plan);
            return (
              <div key={g.key} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden break-inside-avoid">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" style={{ background: '#fafafa' }}>
                  <div>
                    <h2 className="text-base font-bold text-gray-900">{g.title}</h2>
                    <p className="text-xs text-gray-400">{g.note}</p>
                  </div>
                  <span className="text-xs font-medium text-gray-500">
                    {camps.length > 0 ? `${camps.length} kampanya eşleşti` : 'eşleşen kampanya yok'}
                  </span>
                </div>

                <table className="w-full text-sm mt-1">
                  <thead className="text-gray-400 text-xs uppercase">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Metrik</th>
                      <th className="text-right px-4 py-2 font-semibold">Plan</th>
                      <th className="text-right px-4 py-2 font-semibold">Gerçekleşen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {METRIC_ROWS.map((m) => {
                      const pv = plan[m.key];
                      const av = actual[m.key];
                      const noActual = camps.length === 0;
                      return (
                        <tr key={m.key} className="hover:bg-gray-50">
                          <td className="px-4 py-2 text-gray-600">{m.label}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-800">{m.fmt(pv)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-900 font-medium">{noActual ? '—' : m.fmt(av)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* Kampanya kampanya detayı */}
                {camps.length > 0 && (
                  <div className="px-4 pb-4 pt-1 overflow-x-auto">
                    <div className="text-[11px] font-semibold text-gray-400 uppercase mt-3 mb-1">Kampanya detayı</div>
                    <table className="w-full text-xs">
                      <thead className="text-gray-400 uppercase border-b border-gray-100">
                        <tr>
                          <th className="text-left px-2 py-1.5 font-semibold">Kampanya</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Harcama</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Gösterim</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Erişim</th>
                          <th className="text-right px-2 py-1.5 font-semibold">Tıklama</th>
                          <th className="text-right px-2 py-1.5 font-semibold">CTR</th>
                          <th className="text-right px-2 py-1.5 font-semibold">CPM</th>
                          <th className="text-right px-2 py-1.5 font-semibold">CPC</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {camps.map((c) => {
                          const d = deriveRatios({
                            spend: Number(c.spend || 0),
                            impressions: Number(c.impressions || 0),
                            reach: Number(c.reach || 0),
                            clicks: Number(c.clicks || 0),
                          });
                          return (
                            <tr key={c.campaign_id || c.campaign_name} className="hover:bg-gray-50">
                              <td className="px-2 py-1.5 font-mono text-gray-700 max-w-[280px] truncate" title={c.campaign_name}>{c.campaign_name}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney(d.spend)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtInt(d.impressions)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtInt(d.reach)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtInt(d.clicks)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtPct(d.ctr)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney2(d.cpm)}</td>
                              <td className="px-2 py-1.5 text-right tabular-nums">{fmtMoney2(d.cpc)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Eşleşmeyen kampanyalar — şeffaflık için gizlenmez */}
          {grouped.unmatched.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm break-inside-avoid">
              <div className="flex items-center gap-2 font-semibold text-amber-800 mb-2">
                <AlertCircle className="w-4 h-4" /> Hiçbir plana eşleşmeyen {grouped.unmatched.length} kampanya
              </div>
              <div className="space-y-1">
                {grouped.unmatched.map((c) => (
                  <div key={c.campaign_id || c.campaign_name} className="flex justify-between text-amber-900/80">
                    <span className="font-mono text-xs">{c.campaign_name}</span>
                    <span className="tabular-nums text-xs">{fmtMoney(c.spend)} · {fmtInt(c.impressions)} gösterim</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-amber-700 mt-2">Bunlar bir plan grubuna dahil edilmedi. Eşleştirme kuralını değiştirmemi istersen söyle.</p>
            </div>
          )}

          <p className="text-xs text-gray-400 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Kaynak: Meta Graph API (canlı). Plan rakamları /budget/canon-nisan-2026 ile aynı; plan tarihleri yok sayılır, eşleştirme kampanya isminden yapılır.
          </p>
        </div>
      )}
    </div>
  );
}
